import { FC } from 'react'
import { LockOutlined } from '@ant-design/icons'
import VkUnreadCountBadge from '@pages/Admin/CRM/components/integrations/vk/components/VkUnreadCountBadge'
import type { CrmIntegrationConversation } from '../../types/crm-integration-chat.types'
import { formatCrmDateTimeMsk } from '@pages/Admin/CRM/utils/formatData'
import { formatVkUnreadCountBadge } from '@pages/Admin/CRM/utils/leadFields'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import cn from 'classnames'
import classes from '../../styles/crmIntegrationChat.module.css'

const LockIcon = LockOutlined as unknown as FC<{ className?: string }>

const getChatTitle = (item: CrmIntegrationConversation): string => {
  const title = item.title?.trim()
  if (title) {
    return title
  }
  if (item.lead_id != null) {
    return `Лид #${item.lead_id}`
  }
  const recipient = item.external_recipient?.trim()
  if (recipient) {
    return recipient
  }
  return `Диалог #${item.id}`
}

const getInitials = (label: string): string => {
  const parts = label.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

const formatLastMessagePreview = (message: string | null): string => {
  const text = message?.trim()
  if (!text) {
    return 'Нет сообщений'
  }
  return text.length > 120 ? `${text.slice(0, 117)}…` : text
}

type CrmIntegrationChatsListItemProps = {
  item: CrmIntegrationConversation
  variant?: 'modal' | 'dock' | 'page'
  showUnreadBadge?: boolean
  isAccessible?: boolean
  accessDeniedTooltip?: string
  integrationSubtitle?: string | null
  onSelect: (item: CrmIntegrationConversation) => void
}

const CrmIntegrationChatsListItem: FC<CrmIntegrationChatsListItemProps> = ({
  item,
  variant = 'modal',
  showUnreadBadge = true,
  isAccessible = true,
  accessDeniedTooltip = '',
  integrationSubtitle = null,
  onSelect,
}) => {
  const title = getChatTitle(item)
  const preview = formatLastMessagePreview(item.last_message_text)
  const dateLabel = item.updated_at
    ? formatCrmDateTimeMsk(item.updated_at)
    : null
  const unreadCount = item.unread_count ?? 0
  const unreadBadgeLabel =
    isAccessible && showUnreadBadge
      ? formatVkUnreadCountBadge(unreadCount)
      : null

  const content = (
    <>
      <div className={classes.chatsListItemAvatarWrap}>
        <div className={classes.chatsListItemAvatar} aria-hidden>
          {getInitials(title)}
        </div>
        {unreadBadgeLabel ? (
          <VkUnreadCountBadge
            label={unreadBadgeLabel}
            placement="chatsList"
            dock={variant === 'dock' || variant === 'page'}
            aria-label={`Непрочитанных: ${unreadCount}`}
          />
        ) : null}
      </div>
      <div className={classes.chatsListItemBody}>
        <div className={classes.chatsListItemTopRow}>
          <span className={classes.chatsListItemTitle}>{title}</span>
          {dateLabel ? (
            <span className={classes.chatsListItemDate}>{dateLabel}</span>
          ) : null}
        </div>
        {integrationSubtitle ? (
          <span className={classes.chatsListItemIntegration}>{integrationSubtitle}</span>
        ) : null}
        <span className={classes.chatsListItemPreview}>{preview}</span>
      </div>
      {!isAccessible ? (
        <span className={classes.chatsListItemLockBadge} aria-hidden>
          <LockIcon />
        </span>
      ) : null}
    </>
  )

  if (!isAccessible) {
    return (
      <TooltipWrapper placement="top" title={accessDeniedTooltip}>
        <div
          className={cn(classes.chatsListItem, classes.chatsListItemLocked)}
          aria-disabled="true"
          role="listitem"
        >
          {content}
        </div>
      </TooltipWrapper>
    )
  }

  return (
    <button
      type="button"
      className={classes.chatsListItem}
      onClick={() => onSelect(item)}
    >
      {content}
    </button>
  )
}

export default CrmIntegrationChatsListItem
